import React, { useState, useEffect, useCallback, useRef } from "react";
import Confetti from "react-confetti";
import { Line, Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
} from 'chart.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalculator, faChartPie, faChartLine, faTimes, faRetweet, faShieldAlt } from '@fortawesome/free-solid-svg-icons';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
);

// 💰 Budget Bloom - Financial Literacy Game (Mobile-first)
// Learn smart saving & spending through interactive scenarios.
// Rewards: Smart Saver Badges + Shakti Coins

const STORAGE_KEY_WALLET = "sheearns_shakti_wallet_v1";
const STORAGE_KEY_BUDGET = "sheearns_budget_bloom_v1";

export default function BudgetBloom() {
  const [wallet, setWallet] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_WALLET);
      return raw ? JSON.parse(raw) : { coins: 0, totalXP: 0, peacePoints: 0 };
    } catch {
      return { coins: 0, totalXP: 0, peacePoints: 0 };
    }
  });

  const [scenarioIndex, setScenarioIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [budget, setBudget] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  // Debt Payoff Simulator State
  const [showDebtSimulator, setShowDebtSimulator] = useState(false);
  const [debtAmount, setDebtAmount] = useState(0);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [interestRate, setInterestRate] = useState(0.18); // Default 18% APR
  const [payoffTime, setPayoffTime] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [debtStrategy, setDebtStrategy] = useState('avalanche'); // 'avalanche' or 'snowball'
  // Investment Risk Simulator State
  const [showInvestSimulator, setShowInvestSimulator] = useState(false);
  const [investAmount, setInvestAmount] = useState(0);
  const [expectedReturn, setExpectedReturn] = useState(0.12); // Default 12% annual
  const [volatility, setVolatility] = useState(0.15); // Default 15%
  const [years, setYears] = useState(5);
  const [simulationResults, setSimulationResults] = useState({ min: 0, max: 0, avg: 0 });
  // Compound Interest Calculator State
  const [showCompound, setShowCompound] = useState(false);
  const [principal, setPrincipal] = useState(0);
  const [annualRate, setAnnualRate] = useState(0.07);
  const [compoundYears, setCompoundYears] = useState(10);
  const [annualContribution, setAnnualContribution] = useState(0);
  const [compoundsPerYear, setCompoundsPerYear] = useState(12);
  const [futureValue, setFutureValue] = useState(0);
  const [yearlyValues, setYearlyValues] = useState([]);
  // Portfolio Diversification Guide State
  const [showDiversification, setShowDiversification] = useState(false);
  // Retirement Planning Simulator State
  const [showRetirement, setShowRetirement] = useState(false);
  const [currentAge, setCurrentAge] = useState(30);
  const [retirementAge, setRetirementAge] = useState(60);
  const [currentSavings, setCurrentSavings] = useState(0);
  const [monthlyContribution, setMonthlyContribution] = useState(0);
  const [retirementReturn, setRetirementReturn] = useState(0.08);
  const [inflationRate, setInflationRate] = useState(0.06);
  const [retirementCorpus, setRetirementCorpus] = useState(0);
  const [retirementYearlyValues, setRetirementYearlyValues] = useState([]);
  // Tax Optimization Tool State
  const [showTaxOptimizer, setShowTaxOptimizer] = useState(false);
  const [annualIncome, setAnnualIncome] = useState(0);
  const [investments, setInvestments] = useState({ elss: 0, ppf: 0, nps: 0 });
  const [taxSlab, setTaxSlab] = useState('new'); // 'new' or 'old'
  const [taxLiability, setTaxLiability] = useState({ without: 0, with: 0 });
  const [taxSavings, setTaxSavings] = useState(0);

  const debtSimulatorRef = useRef(null);
  const investSimulatorRef = useRef(null);
  const compoundRef = useRef(null);
  const diversificationRef = useRef(null);
  const retirementRef = useRef(null);
  const taxOptimizerRef = useRef(null);

  useEffect(() => {
    setIsMounted(true);
    const updateDimensions = () => {
      setDimensions({ width: window.innerWidth, height: window.innerHeight });
    };
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  // Show options with stagger animation
  useEffect(() => {
    if (!completed) {
      setShowOptions(false);
      const timer = setTimeout(() => setShowOptions(true), 300);
      return () => clearTimeout(timer);
    }
  }, [scenarioIndex, completed]);

  // Calculate debt payoff
  useEffect(() => {
    if (debtAmount > 0 && monthlyPayment > 0 && interestRate > 0) {
      const monthlyRate = interestRate / 12;
      let n = Math.log(monthlyPayment / (monthlyPayment - debtAmount * monthlyRate)) / Math.log(1 + monthlyRate);
      if (isNaN(n) || n < 0) n = (debtAmount * monthlyRate) / (monthlyPayment - debtAmount * monthlyRate);
      const months = Math.ceil(n);
      setPayoffTime(months);
      const totalPaid = months * monthlyPayment;
      setTotalInterest(totalPaid - debtAmount);
    } else {
      setPayoffTime(0);
      setTotalInterest(0);
    }
  }, [debtAmount, monthlyPayment, interestRate]);

  // Simple Monte Carlo for investment simulation
  useEffect(() => {
    if (investAmount > 0 && expectedReturn > 0 && volatility > 0 && years > 0) {
      const simulations = 1000;
      const annualReturn = expectedReturn;
      const stdDev = volatility;
      let results = [];
      for (let i = 0; i < simulations; i++) {
        let value = investAmount;
        for (let y = 0; y < years; y++) {
          const random = (Math.random() - 0.5) * 2;
          const returnRate = annualReturn + (random * stdDev);
          value *= (1 + returnRate);
        }
        results.push(value);
      }
      const min = Math.min(...results);
      const max = Math.max(...results);
      const avg = results.reduce((a, b) => a + b, 0) / simulations;
      setSimulationResults({ min: Math.round(min), max: Math.round(max), avg: Math.round(avg) });
    } else {
      setSimulationResults({ min: 0, max: 0, avg: 0 });
    }
  }, [investAmount, expectedReturn, volatility, years]);

  // Calculate compound interest
  useEffect(() => {
    if (principal > 0 || annualContribution > 0) {
      const r = annualRate / compoundsPerYear;
      const n = compoundsPerYear;
      const t = compoundYears;
      let fv = principal * Math.pow(1 + r, n * t);
      if (annualContribution > 0) {
        fv += annualContribution * ((Math.pow(1 + r, n * t) - 1) / r);
      }
      setFutureValue(Math.round(fv));

      // Yearly values for chart
      const yearly = [];
      let current = principal;
      for (let y = 1; y <= t; y++) {
        current = current * Math.pow(1 + r, n) + annualContribution;
        yearly.push(Math.round(current));
      }
      setYearlyValues(yearly);
    } else {
      setFutureValue(0);
      setYearlyValues([]);
    }
  }, [principal, annualRate, compoundYears, annualContribution, compoundsPerYear]);

  // Calculate retirement corpus
  useEffect(() => {
    const workingYears = retirementAge - currentAge;
    let corpus = currentSavings;
    const monthlyRate = retirementReturn / 12;
    for (let m = 0; m < workingYears * 12; m++) {
      corpus = corpus * (1 + monthlyRate) + monthlyContribution;
    }
    setRetirementCorpus(Math.round(corpus));

    // Yearly values for chart
    const yearly = [];
    let current = currentSavings;
    for (let y = 1; y <= workingYears; y++) {
      for (let mo = 0; mo < 12; mo++) {
        current = current * (1 + monthlyRate) + monthlyContribution;
      }
      yearly.push(Math.round(current));
    }
    setRetirementYearlyValues(yearly);
  }, [currentAge, retirementAge, currentSavings, monthlyContribution, retirementReturn]);

  // Calculate tax liability
  useEffect(() => {
    // Simplified Indian tax calculation for new regime (FY 2025-26 assumed)
    const slabs = taxSlab === 'new' ? [0, 300000, 700000, 1000000, 1200000, 1500000] : [0, 250000, 500000, 1000000]; // Rates omitted for brevity
    let taxableIncome = annualIncome - (investments.elss + investments.ppf + investments.nps); // 80C deductions up to 1.5L
    let withoutOpt = calculateTax(annualIncome);
    let withOpt = calculateTax(taxableIncome);
    setTaxLiability({ without: withoutOpt, with: withOpt });
    setTaxSavings(withoutOpt - withOpt);
  }, [annualIncome, investments, taxSlab]);

  const calculateTax = (income) => {
    // Placeholder simple tax calc
    return Math.max(0, income * 0.3 - 50000); // Simplified
  };

  const playHaptic = useCallback((pattern) => {
    if (typeof window !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  }, []);

  const playSound = useCallback((frequency = 440, duration = 200) => {
    if (typeof window !== 'undefined') {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + duration / 1000);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + duration / 1000);
    }
  }, []);

  const scenarios = [
    {
      question: "You received ₹1000 as a festival gift. What will you do?",
      options: [
        { text: "🎉 Spend on a party", value: -5 },
        { text: "💰 Save ₹700, spend ₹300", value: +10 },
        { text: "📱 Buy gadgets on EMI", value: -10 },
      ],
    },
    {
      question: "Your friend offers a quick profit investment. You…",
      options: [
        { text: "💸 Invest without checking details", value: -10 },
        { text: "🔍 Research and then invest small", value: +10 },
        { text: "🚫 Ignore it completely", value: 0 },
      ],
    },
    {
      question: "You plan a monthly budget. What’s your first step?",
      options: [
        { text: "📊 Track income & expenses", value: +10 },
        { text: "🛒 Buy things first, check balance later", value: -10 },
        { text: "⏭️ Skip tracking to save time", value: -5 },
      ],
    },
    {
      question: "Your car breaks down unexpectedly, costing ₹5000 to repair. You…",
      options: [
        { text: "💳 Use a credit card", value: -10 },
        { text: "🏦 Dip into your emergency fund", value: +5 },
        { text: "🚗 Delay the repair", value: -5 },
      ],
    },
    {
      question: "You get a 10% salary increase. How do you handle the extra income?",
      options: [
        { text: "✈️ Spend on a vacation", value: -5 },
        { text: "📈 Save 50% and invest", value: +10 },
        { text: "🎁 Gift to family", value: 0 },
      ],
    },
    {
      question: "You have high-interest credit card debt. Your strategy is…",
      options: [
        { text: "😴 Ignore it and pay minimum", value: -10 },
        { text: "💪 Pay off aggressively (Avalanche method)", value: +10 },
        { text: "❄️ Snowball method for motivation", value: +8 },
      ],
    },
    {
      question: "Considering multiple debts, you prioritize…",
      options: [
        { text: "🎯 Highest interest first (Avalanche)", value: +10 },
        { text: "📝 Smallest balance first (Snowball)", value: +8 },
        { text: "🎲 Random order", value: -5 },
      ],
    },
  ];

  const handleChoice = (val) => {
    setIsAnimating(true);
    playSound(440, 150); // Choice sound

    // Haptic feedback based on choice
    if (val > 0) {
      playHaptic([10, 50, 10]); // Success vibe
    } else if (val < 0) {
      playHaptic([200]); // Error vibe
    } else {
      playHaptic([100]); // Neutral vibe
    }

    const nextScore = score + val;
    const nextBudget = budget + (val * 100);
    const nextIndex = scenarioIndex + 1;

    setScore(nextScore);
    setBudget(nextBudget);

    if (nextIndex >= scenarios.length) {
      setCompleted(true);
      playSound(523.25, 300); // Completion success sound
      playHaptic([50, 100, 50, 100, 50]); // Celebration vibe
      const coinsEarned = Math.floor(Math.max(0, nextBudget / 100));
      const updated = {
        ...wallet,
        coins: wallet.coins + coinsEarned,
        totalXP: wallet.totalXP + coinsEarned,
      };
      localStorage.setItem(STORAGE_KEY_WALLET, JSON.stringify(updated));
      localStorage.setItem(STORAGE_KEY_BUDGET, JSON.stringify({ completed: true, score: nextScore, budget: nextBudget }));
      setWallet(updated);
    } else {
      setScenarioIndex(nextIndex);
    }

    // Reset animation after delay
    setTimeout(() => setIsAnimating(false), 300);
  };

  const resetGame = () => {
    setIsAnimating(true);
    playSound(659.25, 200); // Reset sound
    playHaptic([150]); // Reset vibe
    setScenarioIndex(0);
    setScore(0);
    setBudget(0);
    setCompleted(false);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const toggleDebtSimulator = () => {
    setShowDebtSimulator(!showDebtSimulator);
    playHaptic([100]);
  };

  const toggleInvestSimulator = () => {
    setShowInvestSimulator(!showInvestSimulator);
    playHaptic([100]);
  };

  const toggleCompound = () => {
    setShowCompound(!showCompound);
    playHaptic([100]);
  };

  const toggleDiversification = () => {
    setShowDiversification(!showDiversification);
    playHaptic([100]);
  };

  const toggleRetirement = () => {
    setShowRetirement(!showRetirement);
    playHaptic([100]);
  };

  const toggleTaxOptimizer = () => {
    setShowTaxOptimizer(!showTaxOptimizer);
    playHaptic([100]);
  };

  const progress = ((scenarioIndex + 1) / scenarios.length) * 100;

  // Enhanced Chart options for interactivity
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    animation: {
      duration: 2000,
      easing: 'easeInOutQuart',
    },
    plugins: {
      legend: {
        labels: { color: 'white' },
        onClick: (e, legendItem, legend) => {
          // Enhanced interactivity: toggle dataset
          const index = legendItem.datasetIndex;
          const ci = legend.chart;
          if (ci.isDatasetVisible(index)) {
            ci.hide(index);
          } else {
            ci.show(index);
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        callbacks: {
          label: function(context) {
            return `₹${context.parsed.y.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      x: { ticks: { color: 'gray' }, grid: { color: 'rgba(255,255,255,0.1)' } },
      y: { ticks: { color: 'gray', callback: function(value) { return '₹' + value.toLocaleString(); } }, grid: { color: 'rgba(255,255,255,0.1)' } }
    },
    onClick: (e, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        // console.log(`Clicked on year: ${index + 1}, Value: ₹${elements[0].parsed.y.toLocaleString()}`);
      }
    }
  };

  const compoundChartData = {
    labels: Array.from({length: compoundYears}, (_, i) => `Year ${i+1}`),
    datasets: [{
      label: 'Portfolio Value (₹)',
      data: yearlyValues,
      borderColor: 'rgb(236, 72, 153)',
      backgroundColor: 'rgba(236, 72, 153, 0.2)',
      tension: 0.4,
      fill: true,
    }]
  };

  const retirementChartData = {
    labels: Array.from({length: retirementAge - currentAge}, (_, i) => `Age ${currentAge + i}`),
    datasets: [{
      label: 'Retirement Corpus (₹)',
      data: retirementYearlyValues,
      borderColor: 'rgb(34, 197, 94)',
      backgroundColor: 'rgba(34, 197, 94, 0.2)',
      tension: 0.4,
      fill: true,
    }]
  };

  const taxChartData = {
    labels: ['Without Optimization', 'With Optimization'],
    datasets: [{
      label: 'Tax Liability (₹)',
      data: [taxLiability.without, taxLiability.with],
      backgroundColor: ['rgba(239, 68, 68, 0.8)', 'rgba(34, 197, 94, 0.8)'],
    }]
  };

  const diversificationData = {
    labels: ['Stocks (60%)', 'Bonds (30%)', 'Cash (10%)'],
    datasets: [{
      data: [60, 30, 10],
      backgroundColor: ['#10b981', '#3b82f6', '#f59e0b'],
    }]
  };

  return (
    <>
      <style jsx global>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;700&display=swap');
        body {
          font-family: 'Poppins', sans-serif;
          margin: 0;
          padding: 0;
          overscroll-behavior: none;
        }
        .immersive-bg {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: -1;
          background: linear-gradient(135deg, #ec4899 0%, #8b5cf6 50%, #06b6d4 100%);
          animation: gradientShift 10s ease infinite;
        }
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .card-enter {
          opacity: 0;
          transform: translateY(20px) scale(0.95);
          transition: all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .card-enter-active {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
        .option-stagger {
          opacity: 0;
          transform: translateX(-20px);
          transition: all 0.4s ease-out;
        }
        .option-stagger:nth-child(1) { transition-delay: 0.1s; }
        .option-stagger:nth-child(2) { transition-delay: 0.2s; }
        .option-stagger:nth-child(3) { transition-delay: 0.3s; }
        .option-enter-active {
          opacity: 1;
          transform: translateX(0);
        }
        .sim-enter {
          opacity: 0;
          transform: scale(0.8) translateY(20px);
          transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .sim-enter-active {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
        .sparkle {
          position: absolute;
          width: 4px;
          height: 4px;
          background: #fbbf24;
          border-radius: 50%;
          animation: sparkleAnim 1.5s infinite ease-in-out;
        }
        @keyframes sparkleAnim {
          0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
          50% { opacity: 1; transform: scale(1) rotate(180deg); }
        }
        .sparkle:nth-child(1) { top: 10%; left: 10%; animation-delay: 0s; }
        .sparkle:nth-child(2) { top: 20%; right: 10%; animation-delay: 0.3s; }
        .sparkle:nth-child(3) { bottom: 20%; left: 20%; animation-delay: 0.6s; }
        .sparkle:nth-child(4) { bottom: 10%; right: 20%; animation-delay: 0.9s; }
        .progress-bar-fill {
          background: linear-gradient(90deg, #10b981, #f59e0b, #ef4444);
          background-size: 200% 100%;
          animation: progressGradient 2s ease infinite;
        }
        @keyframes progressGradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .range-slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: linear-gradient(to bottom, #ec4899, #8b5cf6);
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
        .range-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: linear-gradient(to bottom, #ec4899, #8b5cf6);
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
        .chart-container {
          position: relative;
          height: 250px;
          margin: 1rem 0;
        }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
        @media (max-width: 768px) {
          .min-h-screen {
            padding-bottom: env(safe-area-inset-bottom);
          }
        }
      `}</style>
      <div className="immersive-bg"></div>
      <div className={`min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-800 p-4 flex flex-col items-center text-white relative overflow-hidden transition-all duration-1000 ease-out ${isMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="sparkle"></div>
          <div className="sparkle"></div>
          <div className="sparkle"></div>
          <div className="sparkle"></div>
        </div>
        <h1 className={`text-xl font-bold mb-2 text-pink-300 transition-all duration-1000 ease-out ${isMounted ? 'opacity-100 translate-y-0 delay-200' : 'opacity-0 translate-y-4'}`}>💰 Budget Bloom</h1>
        <p className={`text-xs text-gray-300 mb-6 text-center max-w-xs transition-all duration-1000 ease-out ${isMounted ? 'opacity-100 translate-y-0 delay-300' : 'opacity-0 translate-y-4'}`}>
          Make smart financial choices and watch your digital savings grow 🌱
        </p>
        <div className="w-full max-w-md relative z-10">

          {!completed ? (
            <div 
              className={`bg-gray-800/90 backdrop-blur-sm rounded-2xl p-6 shadow-2xl border border-pink-800/30 transition-all duration-1000 ease-out transform card-enter ${isAnimating ? 'scale-95 opacity-75' : 'scale-100 opacity-100'} ${isMounted ? 'card-enter-active delay-500' : ''}`}
              role="main"
              aria-labelledby="game-title"
            >
              <div
                className="w-full bg-gray-700/50 rounded-full h-3 mb-4 overflow-hidden relative"
                role="progressbar"
                aria-label="Game progress"
                aria-valuenow={progress}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                <div
                  className="progress-bar-fill h-3 rounded-full transition-all duration-700 ease-out shadow-lg relative overflow-hidden"
                  style={{ width: `${progress}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                </div>
              </div>
              <style jsx>{`
                @keyframes shimmer {
                  0% { transform: translateX(-100%); }
                  100% { transform: translateX(100%); }
                }
                .animate-shimmer {
                  animation: shimmer 2s infinite;
                }
              `}</style>
              <p className={`text-base text-gray-200 mb-4 font-semibold text-center transition-all duration-500 ${isAnimating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}>
                💎 Current Savings: <span className="text-pink-400">₹{budget.toLocaleString()}</span>
              </p>
              <h2 id="game-title" className={`text-xl font-semibold mb-6 text-pink-200 text-center transition-all duration-500 ${isAnimating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}>
                {scenarios[scenarioIndex].question}
              </h2>
              <div className="space-y-3" role="radiogroup" aria-labelledby="options-label">
                <span id="options-label" className="sr-only">Choose an option</span>
                {scenarios[scenarioIndex].options.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => handleChoice(opt.value)}
                    className={`w-full py-4 px-5 bg-gradient-to-r from-pink-700/80 to-purple-600/80 hover:from-pink-600/90 hover:to-purple-500/90 active:from-pink-500/90 active:to-purple-400/90 focus:outline-none focus:ring-2 focus:ring-pink-400/50 focus:ring-offset-2 focus:ring-offset-gray-800/50 rounded-2xl text-left text-white transition-all duration-300 ease-out shadow-lg hover:shadow-2xl hover:scale-102 active:scale-98 transform group relative overflow-hidden ${showOptions ? 'option-stagger option-enter-active' : 'option-stagger'}`}
                    aria-label={`${opt.text} (Score impact: ${opt.value > 0 ? '+' : ''}${opt.value})`}
                    role="radio"
                    aria-checked={false}
                  >
                    <div className="absolute inset-0 bg-white/10 rounded-2xl scale-0 group-hover:scale-100 transition-transform duration-300 origin-center"></div>
                    <span className="relative z-10 transition-all duration-300 group-hover:translate-x-2 flex items-center">
                      <span className="mr-3 text-lg">{opt.text.split(' ')[0]}</span>
                      <span className="font-medium">{opt.text.split(' ').slice(1).join(' ')}</span>
                    </span>
                  </button>
                ))}
              </div>
              <div className={`mt-6 text-xs text-center text-gray-400 font-medium transition-all duration-500 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}>
                📍 Question {scenarioIndex + 1} of {scenarios.length}
              </div>
            </div>
          ) : (
            <div className={`bg-gray-800/90 backdrop-blur-sm rounded-2xl p-8 shadow-2xl w-full text-center border border-pink-800/30 z-10 relative transition-all duration-1000 ease-out transform card-enter ${isAnimating ? 'scale-95 opacity-75' : 'scale-100 opacity-100'} ${isMounted ? 'card-enter-active delay-500' : ''}`}>
              <div className="mb-4">
                <div className="text-4xl animate-bounce">🎉</div>
              </div>
              <h2 className="text-2xl font-bold mb-4 text-pink-300">Game Completed!</h2>
              <div className="space-y-2 mb-6">
                <p className="text-lg text-gray-200">Your Smart Score: <span className="text-pink-400 font-bold">{score}</span></p>
                <p className="text-lg text-gray-200">Final Savings: <span className="text-green-400 font-bold">₹{budget.toLocaleString()}</span></p>
                <p className="text-base text-gray-300">
                  You earned <span className="text-yellow-400 font-bold text-xl animate-pulse">{Math.floor(Math.max(0, budget / 100))} Shakti Coins</span>! 🌟
                </p>
              </div>
              <div className="space-y-3">
                <button
                  onClick={resetGame}
                  className="bg-gradient-to-r from-pink-600/90 to-purple-500/90 text-white rounded-2xl px-8 py-3 text-base font-semibold hover:from-pink-500/90 hover:to-purple-400/90 active:from-pink-400/90 active:to-purple-300/90 focus:outline-none focus:ring-2 focus:ring-pink-400/50 focus:ring-offset-2 focus:ring-offset-gray-800/50 transition-all duration-300 ease-out shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transform group relative overflow-hidden w-full"
                  aria-label="Play the game again"
                >
                  <div className="absolute inset-0 bg-white/20 rounded-2xl scale-0 group-hover:scale-100 transition-transform duration-300 origin-center"></div>
                  <span className="relative z-10 transition-all duration-300 group-hover:translate-x-1 flex items-center justify-center">
                    🔄 Play Again
                  </span>
                </button>
                <button
                  onClick={toggleDebtSimulator}
                  className="bg-gradient-to-r from-red-600/90 to-orange-500/90 text-white rounded-2xl px-8 py-3 text-base font-semibold hover:from-red-500/90 hover:to-orange-400/90 active:from-red-400/90 active:to-orange-300/90 focus:outline-none focus:ring-2 focus:ring-red-400/50 focus:ring-offset-2 focus:ring-offset-gray-800/50 transition-all duration-300 ease-out shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transform group relative overflow-hidden w-full flex items-center justify-center"
                  aria-label="Open Debt Payoff Simulator"
                >
                  <FontAwesomeIcon icon={faCalculator} className="mr-2" />
                  💳 Debt Strategies Simulator
                </button>
                <button
                  onClick={toggleInvestSimulator}
                  className="bg-gradient-to-r from-green-600/90 to-blue-500/90 text-white rounded-2xl px-8 py-3 text-base font-semibold hover:from-green-500/90 hover:to-blue-400/90 active:from-green-400/90 active:to-blue-300/90 focus:outline-none focus:ring-2 focus:ring-green-400/50 focus:ring-offset-2 focus:ring-offset-gray-800/50 transition-all duration-300 ease-out shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transform group relative overflow-hidden w-full flex items-center justify-center"
                  aria-label="Open Investment Risk Simulator"
                >
                  <FontAwesomeIcon icon={faChartLine} className="mr-2" />
                  📈 Investment Risk Simulator
                </button>
                <button
                  onClick={toggleCompound}
                  className="bg-gradient-to-r from-yellow-600/90 to-amber-500/90 text-white rounded-2xl px-8 py-3 text-base font-semibold hover:from-yellow-500/90 hover:to-amber-400/90 active:from-yellow-400/90 active:to-amber-300/90 focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:ring-offset-2 focus:ring-offset-gray-800/50 transition-all duration-300 ease-out shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transform group relative overflow-hidden w-full flex items-center justify-center"
                  aria-label="Open Compound Interest Calculator"
                >
                  <FontAwesomeIcon icon={faChartLine} className="mr-2" />
                  🧮 Compound Interest Calculator
                </button>
                <button
                  onClick={toggleDiversification}
                  className="bg-gradient-to-r from-indigo-600/90 to-violet-500/90 text-white rounded-2xl px-8 py-3 text-base font-semibold hover:from-indigo-500/90 hover:to-violet-400/90 active:from-indigo-400/90 active:to-violet-300/90 focus:outline-none focus:ring-2 focus:ring-indigo-400/50 focus:ring-offset-2 focus:ring-offset-gray-800/50 transition-all duration-300 ease-out shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transform group relative overflow-hidden w-full flex items-center justify-center"
                  aria-label="Open Portfolio Diversification Guide"
                >
                  <FontAwesomeIcon icon={faChartPie} className="mr-2" />
                  🛡️ Portfolio Diversification Guide
                </button>
                <button
                  onClick={toggleRetirement}
                  className="bg-gradient-to-r from-purple-600/90 to-pink-500/90 text-white rounded-2xl px-8 py-3 text-base font-semibold hover:from-purple-500/90 hover:to-pink-400/90 active:from-purple-400/90 active:to-pink-300/90 focus:outline-none focus:ring-2 focus:ring-purple-400/50 focus:ring-offset-2 focus:ring-offset-gray-800/50 transition-all duration-300 ease-out shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transform group relative overflow-hidden w-full flex items-center justify-center"
                  aria-label="Open Retirement Planning Simulator"
                >
                  <FontAwesomeIcon icon={faRetweet} className="mr-2" />
                  🏖️ Retirement Planning Simulator
                </button>
                <button
                  onClick={toggleTaxOptimizer}
                  className="bg-gradient-to-r from-teal-600/90 to-cyan-500/90 text-white rounded-2xl px-8 py-3 text-base font-semibold hover:from-teal-500/90 hover:to-cyan-400/90 active:from-teal-400/90 active:to-cyan-300/90 focus:outline-none focus:ring-2 focus:ring-teal-400/50 focus:ring-offset-2 focus:ring-offset-gray-800/50 transition-all duration-300 ease-out shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transform group relative overflow-hidden w-full flex items-center justify-center"
                  aria-label="Open Tax Optimization Tool"
                >
                  <FontAwesomeIcon icon={faShieldAlt} className="mr-2" />
                  💼 Tax Optimization Tool
                </button>
              </div>
            </div>
          )}

        </div>

        {completed && (
          <Confetti
            width={dimensions.width}
            height={dimensions.height}
            recycle={false}
            numberOfPieces={300}
            gravity={0.1}
            colors={['#ec4899', '#a855f7', '#06b6d4', '#f59e0b', '#10b981']}
          />
        )}

        <footer className={`text-xs text-gray-400 mt-8 text-center z-10 relative transition-all duration-1000 ease-out ${isMounted ? 'opacity-100 translate-y-0 delay-700' : 'opacity-0 translate-y-4'}`}>
          Part of <span className="font-semibold text-pink-300">SheEarns</span> Empowerment Games 🌸
        </footer>
      </div>

      {/* Debt Payoff Simulator Modal */}
      {showDebtSimulator && (
        <div 
          className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 sim-enter ${isMounted ? 'sim-enter-active' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="debt-sim-title"
          onClick={(e) => { if (e.target === e.currentTarget) toggleDebtSimulator(); }}
        >
          <div 
            ref={debtSimulatorRef}
            className="bg-gradient-to-b from-gray-800/95 to-gray-900/95 backdrop-blur-md rounded-3xl p-6 shadow-2xl border border-red-800/30 w-full max-w-md max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="absolute top-2 right-2 text-gray-400 hover:text-white text-xl" onClick={toggleDebtSimulator} aria-label="Close">
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h2 id="debt-sim-title" className="text-2xl font-bold mb-4 text-red-300 text-center animate-pulse">💳 Advanced Debt Strategies</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="debt-amount" className="block text-sm font-medium text-gray-300 mb-1">Initial Debt Amount (₹)</label>
                <input
                  id="debt-amount"
                  type="number"
                  value={debtAmount}
                  onChange={(e) => setDebtAmount(Number(e.target.value))}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-400 border border-gray-600 transition-all"
                  placeholder="e.g., 50000"
                  aria-describedby="debt-help"
                  min="0"
                  step="1000"
                />
                <p id="debt-help" className="text-xs text-gray-500 mt-1">Enter your total debt.</p>
              </div>
              <div>
                <label htmlFor="monthly-payment" className="block text-sm font-medium text-gray-300 mb-1">Monthly Payment (₹)</label>
                <input
                  id="monthly-payment"
                  type="number"
                  value={monthlyPayment}
                  onChange={(e) => setMonthlyPayment(Number(e.target.value))}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-400 border border-gray-600 transition-all"
                  placeholder="e.g., 5000"
                  aria-describedby="payment-help"
                  min="0"
                  step="500"
                />
                <p id="payment-help" className="text-xs text-gray-500 mt-1">How much can you pay monthly?</p>
              </div>
              <div>
                <label htmlFor="interest-rate" className="block text-sm font-medium text-gray-300 mb-1">Annual Interest Rate (%)</label>
                <input
                  id="interest-rate"
                  type="number"
                  value={interestRate * 100}
                  onChange={(e) => setInterestRate(Number(e.target.value) / 100)}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-400 border border-gray-600 transition-all"
                  placeholder="e.g., 18"
                  aria-describedby="rate-help"
                  min="0"
                  max="100"
                  step="0.1"
                />
                <p id="rate-help" className="text-xs text-gray-500 mt-1">Typical credit card rate is 18-24%.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Strategy</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="debt-strategy"
                      value="avalanche"
                      checked={debtStrategy === 'avalanche'}
                      onChange={(e) => setDebtStrategy(e.target.value)}
                      className="mr-2 text-red-500 focus:ring-red-400"
                    />
                    <span className="text-sm text-gray-300">Avalanche (High interest first - Saves money)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="debt-strategy"
                      value="snowball"
                      checked={debtStrategy === 'snowball'}
                      onChange={(e) => setDebtStrategy(e.target.value)}
                      className="mr-2 text-red-500 focus:ring-red-400"
                    />
                    <span className="text-sm text-gray-300">Snowball (Smallest first - Builds momentum)</span>
                  </label>
                </div>
              </div>
              <button
                onClick={() => {}}
                className="w-full bg-gradient-to-r from-red-600 to-orange-500 text-white rounded-xl px-6 py-3 font-semibold hover:from-red-500 hover:to-orange-400 focus:outline-none focus:ring-2 focus:ring-red-400 transition-all duration-300 shadow-lg hover:shadow-xl animate-pulse"
                aria-label="Simulate payoff"
              >
                Simulate Payoff
              </button>
              {payoffTime > 0 && (
                <div className="bg-gradient-to-r from-green-900/50 to-blue-900/50 rounded-xl p-4 text-center border border-green-500/30 animate-bounce">
                  <p className="text-lg font-bold text-green-300">Time to Pay Off: {Math.ceil(payoffTime / 12)} years ({payoffTime} months)</p>
                  <p className="text-sm text-gray-300">Total Interest Paid: ₹{totalInterest.toLocaleString()}</p>
                  <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                    <div className="bg-green-500 h-2 rounded-full transition-all duration-1000" style={{ width: `${Math.min(100, (payoffTime / 360) * 100)}%` }}></div>
                  </div>
                </div>
              )}
              <button
                onClick={toggleDebtSimulator}
                className="w-full bg-gray-600/50 text-white rounded-xl px-6 py-3 font-semibold hover:bg-gray-500/50 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-all duration-300 border border-gray-500/30"
                aria-label="Close Debt Simulator"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Investment Risk Simulator Modal */}
      {showInvestSimulator && (
        <div 
          className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 sim-enter ${isMounted ? 'sim-enter-active' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="invest-sim-title"
          onClick={(e) => { if (e.target === e.currentTarget) toggleInvestSimulator(); }}
        >
          <div 
            ref={investSimulatorRef}
            className="bg-gradient-to-b from-gray-800/95 to-gray-900/95 backdrop-blur-md rounded-3xl p-6 shadow-2xl border border-green-800/30 w-full max-w-md max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="absolute top-2 right-2 text-gray-400 hover:text-white text-xl" onClick={toggleInvestSimulator} aria-label="Close">
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h2 id="invest-sim-title" className="text-2xl font-bold mb-4 text-green-300 text-center animate-pulse">📈 Investment Risk Simulator</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="invest-amount" className="block text-sm font-medium text-gray-300 mb-1">Initial Investment (₹)</label>
                <input
                  id="invest-amount"
                  type="number"
                  value={investAmount}
                  onChange={(e) => setInvestAmount(Number(e.target.value))}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-400 border border-gray-600 transition-all"
                  placeholder="e.g., 100000"
                  min="0"
                  step="10000"
                />
              </div>
              <div>
                <label htmlFor="expected-return" className="block text-sm font-medium text-gray-300 mb-1">Expected Annual Return (%)</label>
                <input
                  id="expected-return"
                  type="number"
                  value={expectedReturn * 100}
                  onChange={(e) => setExpectedReturn(Number(e.target.value) / 100)}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-400 border border-gray-600 transition-all"
                  placeholder="e.g., 12"
                  min="0"
                  step="0.1"
                />
              </div>
              <div>
                <label htmlFor="volatility" className="block text-sm font-medium text-gray-300 mb-1">Volatility (Std Dev %)</label>
                <input
                  id="volatility"
                  type="number"
                  value={volatility * 100}
                  onChange={(e) => setVolatility(Number(e.target.value) / 100)}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-400 border border-gray-600 transition-all"
                  placeholder="e.g., 15"
                  min="0"
                  step="0.1"
                />
              </div>
              <div>
                <label htmlFor="years" className="block text-sm font-medium text-gray-300 mb-1">Investment Horizon (Years)</label>
                <input
                  id="years"
                  type="number"
                  value={years}
                  onChange={(e) => setYears(Number(e.target.value))}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-400 border border-gray-600 transition-all"
                  placeholder="e.g., 5"
                  min="1"
                  max="30"
                />
              </div>
              <button
                onClick={() => {}}
                className="w-full bg-gradient-to-r from-green-600 to-blue-500 text-white rounded-xl px-6 py-3 font-semibold hover:from-green-500 hover:to-blue-400 focus:outline-none focus:ring-2 focus:ring-green-400 transition-all duration-300 shadow-lg hover:shadow-xl animate-pulse"
                aria-label="Run simulation"
              >
                Run Simulation
              </button>
              {simulationResults.avg > 0 && (
                <div className="bg-gradient-to-r from-green-900/50 to-blue-900/50 rounded-xl p-4 text-center border border-green-500/30 animate-bounce space-y-2">
                  <p className="text-lg font-bold text-green-300">Projected Value: ₹{simulationResults.avg.toLocaleString()}</p>
                  <p className="text-sm text-gray-300">Best Case: ₹{simulationResults.max.toLocaleString()} | Worst Case: ₹{simulationResults.min.toLocaleString()}</p>
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>Worst</span><span>Avg</span><span>Best</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-red-500 to-green-500 h-2 rounded-full transition-all duration-1000"
                      style={{ 
                        backgroundSize: '200% 100%',
                        width: '100%',
                        clipPath: `inset(0 ${100 - (simulationResults.min / simulationResults.max * 100)}% 0 0)` 
                      }}
                    ></div>
                  </div>
                </div>
              )}
              <button
                onClick={toggleInvestSimulator}
                className="w-full bg-gray-600/50 text-white rounded-xl px-6 py-3 font-semibold hover:bg-gray-500/50 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-all duration-300 border border-gray-500/30"
                aria-label="Close Investment Simulator"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compound Interest Calculator Modal */}
      {showCompound && (
        <div 
          className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 sim-enter ${isMounted ? 'sim-enter-active' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="compound-title"
          onClick={(e) => { if (e.target === e.currentTarget) toggleCompound(); }}
        >
          <div 
            ref={compoundRef}
            className="bg-gradient-to-b from-gray-800/95 to-gray-900/95 backdrop-blur-md rounded-3xl p-6 shadow-2xl border border-yellow-800/30 w-full max-w-md max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="absolute top-2 right-2 text-gray-400 hover:text-white text-xl" onClick={toggleCompound} aria-label="Close">
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h2 id="compound-title" className="text-2xl font-bold mb-4 text-yellow-300 text-center animate-pulse">🧮 Compound Interest Calculator</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="principal" className="block text-sm font-medium text-gray-300 mb-1">Initial Principal (₹)</label>
                <input
                  id="principal"
                  type="number"
                  value={principal}
                  onChange={(e) => setPrincipal(Number(e.target.value))}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 border border-gray-600 transition-all"
                  placeholder="e.g., 10000"
                  min="0"
                  step="1000"
                />
              </div>
              <div>
                <label htmlFor="annual-rate" className="block text-sm font-medium text-gray-300 mb-1">Annual Interest Rate (%)</label>
                <input
                  id="annual-rate"
                  type="number"
                  value={annualRate * 100}
                  onChange={(e) => setAnnualRate(Number(e.target.value) / 100)}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 border border-gray-600 transition-all"
                  placeholder="e.g., 7"
                  min="0"
                  step="0.1"
                />
              </div>
              <div>
                <label htmlFor="compound-years" className="block text-sm font-medium text-gray-300 mb-1">Years</label>
                <input
                  id="compound-years"
                  type="number"
                  value={compoundYears}
                  onChange={(e) => setCompoundYears(Number(e.target.value))}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 border border-gray-600 transition-all"
                  placeholder="e.g., 10"
                  min="1"
                  max="50"
                />
              </div>
              <div>
                <label htmlFor="annual-contribution" className="block text-sm font-medium text-gray-300 mb-1">Annual Contribution (₹)</label>
                <input
                  id="annual-contribution"
                  type="number"
                  value={annualContribution}
                  onChange={(e) => setAnnualContribution(Number(e.target.value))}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 border border-gray-600 transition-all"
                  placeholder="e.g., 12000"
                  min="0"
                  step="1000"
                />
              </div>
              <div>
                <label htmlFor="compounds-per-year" className="block text-sm font-medium text-gray-300 mb-1">Compounds Per Year</label>
                <select
                  id="compounds-per-year"
                  value={compoundsPerYear}
                  onChange={(e) => setCompoundsPerYear(Number(e.target.value))}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-yellow-400 border border-gray-600"
                >
                  <option value={1}>Annually</option>
                  <option value={4}>Quarterly</option>
                  <option value={12}>Monthly</option>
                  <option value={365}>Daily</option>
                </select>
              </div>
              {futureValue > 0 && (
                <div className="bg-gradient-to-r from-green-900/50 to-yellow-900/50 rounded-xl p-4 text-center border border-green-500/30 animate-bounce">
                  <p className="text-lg font-bold text-green-300">Future Value: ₹{futureValue.toLocaleString()}</p>
                  <div className="chart-container mt-4">
                    <Line data={compoundChartData} options={chartOptions} />
                  </div>
                </div>
              )}
              <button
                onClick={() => {}}
                className="w-full bg-gradient-to-r from-yellow-600 to-amber-500 text-white rounded-xl px-6 py-3 font-semibold hover:from-yellow-500 hover:to-amber-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all duration-300 shadow-lg hover:shadow-xl animate-pulse"
                aria-label="Calculate"
              >
                Calculate
              </button>
              <button
                onClick={toggleCompound}
                className="w-full bg-gray-600/50 text-white rounded-xl px-6 py-3 font-semibold hover:bg-gray-500/50 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-all duration-300 border border-gray-500/30"
                aria-label="Close Compound Calculator"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Portfolio Diversification Guide Modal */}
      {showDiversification && (
        <div 
          className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 sim-enter ${isMounted ? 'sim-enter-active' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="diversification-title"
          onClick={(e) => { if (e.target === e.currentTarget) toggleDiversification(); }}
        >
          <div 
            ref={diversificationRef}
            className="bg-gradient-to-b from-gray-800/95 to-gray-900/95 backdrop-blur-md rounded-3xl p-6 shadow-2xl border border-indigo-800/30 w-full max-w-md max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="absolute top-2 right-2 text-gray-400 hover:text-white text-xl" onClick={toggleDiversification} aria-label="Close">
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h2 id="diversification-title" className="text-2xl font-bold mb-4 text-indigo-300 text-center animate-pulse">🛡️ Portfolio Diversification Guide</h2>
            <div className="space-y-4 text-sm text-gray-300">
              <p><strong>Why Diversify?</strong> Spreading investments reduces risk. Don't put all eggs in one basket!</p>
              <p><strong>Steps:</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Assess risk tolerance</li>
                <li>Mix asset classes: Stocks, Bonds, Cash</li>
                <li>Geographic & sector diversity</li>
                <li>Rebalance annually</li>
              </ul>
              <p><strong>Example Balanced Portfolio:</strong></p>
              <div className="chart-container">
                <Pie data={diversificationData} options={{ ...chartOptions, animation: { duration: 3000, easing: 'easeInBack' } }} />
              </div>
              <p className="text-center text-xs">Aim for 60% Stocks, 30% Bonds, 10% Cash for moderate risk.</p>
            </div>
            <button
              onClick={toggleDiversification}
              className="w-full bg-gray-600/50 text-white rounded-xl px-6 py-3 font-semibold hover:bg-gray-500/50 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-all duration-300 border border-gray-500/30 mt-4"
              aria-label="Close Diversification Guide"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Retirement Planning Simulator Modal */}
      {showRetirement && (
        <div 
          className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 sim-enter ${isMounted ? 'sim-enter-active' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="retirement-title"
          onClick={(e) => { if (e.target === e.currentTarget) toggleRetirement(); }}
        >
          <div 
            ref={retirementRef}
            className="bg-gradient-to-b from-gray-800/95 to-gray-900/95 backdrop-blur-md rounded-3xl p-6 shadow-2xl border border-purple-800/30 w-full max-w-md max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="absolute top-2 right-2 text-gray-400 hover:text-white text-xl" onClick={toggleRetirement} aria-label="Close">
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h2 id="retirement-title" className="text-2xl font-bold mb-4 text-purple-300 text-center animate-pulse">🏖️ Retirement Planning Simulator</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="current-age" className="block text-sm font-medium text-gray-300 mb-1">Current Age</label>
                <input
                  id="current-age"
                  type="number"
                  value={currentAge}
                  onChange={(e) => setCurrentAge(Number(e.target.value))}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 border border-gray-600 transition-all"
                  min="18"
                  max="65"
                />
              </div>
              <div>
                <label htmlFor="retirement-age" className="block text-sm font-medium text-gray-300 mb-1">Retirement Age</label>
                <input
                  id="retirement-age"
                  type="number"
                  value={retirementAge}
                  onChange={(e) => setRetirementAge(Number(e.target.value))}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 border border-gray-600 transition-all"
                  min="50"
                  max="70"
                />
              </div>
              <div>
                <label htmlFor="current-savings" className="block text-sm font-medium text-gray-300 mb-1">Current Savings (₹)</label>
                <input
                  id="current-savings"
                  type="number"
                  value={currentSavings}
                  onChange={(e) => setCurrentSavings(Number(e.target.value))}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 border border-gray-600 transition-all"
                  placeholder="e.g., 500000"
                  min="0"
                />
              </div>
              <div>
                <label htmlFor="monthly-contribution" className="block text-sm font-medium text-gray-300 mb-1">Monthly Contribution (₹)</label>
                <input
                  id="monthly-contribution"
                  type="number"
                  value={monthlyContribution}
                  onChange={(e) => setMonthlyContribution(Number(e.target.value))}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 border border-gray-600 transition-all"
                  placeholder="e.g., 10000"
                  min="0"
                />
              </div>
              <div>
                <label htmlFor="retirement-return" className="block text-sm font-medium text-gray-300 mb-1">Expected Annual Return (%)</label>
                <input
                  id="retirement-return"
                  type="number"
                  value={retirementReturn * 100}
                  onChange={(e) => setRetirementReturn(Number(e.target.value) / 100)}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 border border-gray-600 transition-all"
                  placeholder="e.g., 8"
                  min="0"
                  step="0.1"
                />
              </div>
              <div>
                <label htmlFor="inflation-rate" className="block text-sm font-medium text-gray-300 mb-1">Inflation Rate (%)</label>
                <input
                  id="inflation-rate"
                  type="number"
                  value={inflationRate * 100}
                  onChange={(e) => setInflationRate(Number(e.target.value) / 100)}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 border border-gray-600 transition-all"
                  placeholder="e.g., 6"
                  min="0"
                  step="0.1"
                />
              </div>
              {retirementCorpus > 0 && (
                <div className="bg-gradient-to-r from-green-900/50 to-purple-900/50 rounded-xl p-4 text-center border border-green-500/30 animate-bounce">
                  <p className="text-lg font-bold text-green-300">Projected Corpus: ₹{retirementCorpus.toLocaleString()}</p>
                  <div className="chart-container mt-4">
                    <Line data={retirementChartData} options={chartOptions} />
                  </div>
                </div>
              )}
              <button
                onClick={() => {}}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl px-6 py-3 font-semibold hover:from-purple-500 hover:to-pink-400 focus:outline-none focus:ring-2 focus:ring-purple-400 transition-all duration-300 shadow-lg hover:shadow-xl animate-pulse"
                aria-label="Simulate Retirement"
              >
                Simulate
              </button>
              <button
                onClick={toggleRetirement}
                className="w-full bg-gray-600/50 text-white rounded-xl px-6 py-3 font-semibold hover:bg-gray-500/50 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-all duration-300 border border-gray-500/30"
                aria-label="Close Retirement Simulator"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tax Optimization Tool Modal */}
      {showTaxOptimizer && (
        <div 
          className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 sim-enter ${isMounted ? 'sim-enter-active' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="tax-title"
          onClick={(e) => { if (e.target === e.currentTarget) toggleTaxOptimizer(); }}
        >
          <div 
            ref={taxOptimizerRef}
            className="bg-gradient-to-b from-gray-800/95 to-gray-900/95 backdrop-blur-md rounded-3xl p-6 shadow-2xl border border-teal-800/30 w-full max-w-md max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="absolute top-2 right-2 text-gray-400 hover:text-white text-xl" onClick={toggleTaxOptimizer} aria-label="Close">
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h2 id="tax-title" className="text-2xl font-bold mb-4 text-teal-300 text-center animate-pulse">💼 Tax Optimization Tool</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="annual-income" className="block text-sm font-medium text-gray-300 mb-1">Annual Income (₹)</label>
                <input
                  id="annual-income"
                  type="number"
                  value={annualIncome}
                  onChange={(e) => setAnnualIncome(Number(e.target.value))}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400 border border-gray-600 transition-all"
                  placeholder="e.g., 1000000"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Tax Regime</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="tax-slab"
                      value="new"
                      checked={taxSlab === 'new'}
                      onChange={(e) => setTaxSlab(e.target.value)}
                      className="mr-2 text-teal-500 focus:ring-teal-400"
                    />
                    <span className="text-sm text-gray-300">New Regime</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="tax-slab"
                      value="old"
                      checked={taxSlab === 'old'}
                      onChange={(e) => setTaxSlab(e.target.value)}
                      className="mr-2 text-teal-500 focus:ring-teal-400"
                    />
                    <span className="text-sm text-gray-300">Old Regime</span>
                  </label>
                </div>
              </div>
              <div>
                <label htmlFor="elss" className="block text-sm font-medium text-gray-300 mb-1">ELSS Investment (₹)</label>
                <input
                  id="elss"
                  type="number"
                  value={investments.elss}
                  onChange={(e) => setInvestments({...investments, elss: Number(e.target.value)})}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400 border border-gray-600 transition-all"
                  placeholder="e.g., 50000"
                  min="0"
                  max="150000"
                />
              </div>
              <div>
                <label htmlFor="ppf" className="block text-sm font-medium text-gray-300 mb-1">PPF Investment (₹)</label>
                <input
                  id="ppf"
                  type="number"
                  value={investments.ppf}
                  onChange={(e) => setInvestments({...investments, ppf: Number(e.target.value)})}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400 border border-gray-600 transition-all"
                  placeholder="e.g., 50000"
                  min="0"
                  max="150000"
                />
              </div>
              <div>
                <label htmlFor="nps" className="block text-sm font-medium text-gray-300 mb-1">NPS Investment (₹)</label>
                <input
                  id="nps"
                  type="number"
                  value={investments.nps}
                  onChange={(e) => setInvestments({...investments, nps: Number(e.target.value)})}
                  className="w-full p-3 bg-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400 border border-gray-600 transition-all"
                  placeholder="e.g., 50000"
                  min="0"
                  max="50000"
                />
              </div>
              {taxSavings > 0 && (
                <div className="bg-gradient-to-r from-green-900/50 to-teal-900/50 rounded-xl p-4 text-center border border-green-500/30 animate-bounce">
                  <p className="text-lg font-bold text-green-300">Tax Savings: ₹{taxSavings.toLocaleString()}</p>
                  <div className="chart-container mt-4">
                    <Bar data={taxChartData} options={chartOptions} />
                  </div>
                </div>
              )}
              <button
                onClick={() => {}}
                className="w-full bg-gradient-to-r from-teal-600 to-cyan-500 text-white rounded-xl px-6 py-3 font-semibold hover:from-teal-500 hover:to-cyan-400 focus:outline-none focus:ring-2 focus:ring-teal-400 transition-all duration-300 shadow-lg hover:shadow-xl animate-pulse"
                aria-label="Optimize Tax"
              >
                Optimize
              </button>
              <button
                onClick={toggleTaxOptimizer}
                className="w-full bg-gray-600/50 text-white rounded-xl px-6 py-3 font-semibold hover:bg-gray-500/50 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-all duration-300 border border-gray-500/30"
                aria-label="Close Tax Optimizer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}